import { Alert, Button, Upload } from 'antd';
import type {
  RcFile,
  UploadFile,
  UploadProps,
} from 'antd/es/upload/interface';
import clsx from 'clsx';
import {
  MAX_UPLOAD_FILESIZE,
  MAX_UPLOAD_FILESIZE_BYTES,
} from 'const/attachments';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface InlineFileUploadProps {
  browseLabel: string;
  maxSizeLabel: string;
  multiple?: boolean;
  maxCount?: number;
  accept?: string;
  className?: string;
  fileList?: UploadFile[];
  onChange?: UploadProps['onChange'];
}

export default function InlineFileUpload({
  browseLabel,
  maxSizeLabel,
  multiple = true,
  maxCount = 10,
  accept = '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx',
  className,
  fileList,
  onChange,
}: InlineFileUploadProps) {
  const { t } = useTranslation();
  const [fileSizeErrors, setFileSizeErrors] = useState<string[]>([]);

  const beforeUpload = useCallback(
    (file: RcFile) => {
      const isTooBig = file.size > MAX_UPLOAD_FILESIZE_BYTES;

      if (isTooBig) {
        const errorMessage = t('attachments:file_size_error', {
          filename: file.name,
          maxSize: MAX_UPLOAD_FILESIZE,
        });

        setFileSizeErrors((prev) => [
          ...prev.filter((err) => !err.includes(file.name)),
          errorMessage,
        ]);

        return Upload.LIST_IGNORE;
      }

      setFileSizeErrors((prev) =>
        prev.filter((err) => !err.includes(file.name)),
      );

      return false;
    },
    [t],
  );

  return (
    <div className="flex flex-col gap-2">
      <Upload
        multiple={multiple}
        maxCount={maxCount}
        className={clsx('[&>.ant-upload]:w-full', className)}
        beforeUpload={beforeUpload}
        customRequest={({ onSuccess }) => {
          onSuccess?.('ok');
        }}
        fileList={fileList}
        onChange={onChange}
        onRemove={(file) => {
          setFileSizeErrors((prev) =>
            prev.filter((err) => !err.includes(file.name)),
          );
          return true;
        }}
        accept={accept}
      >
        <div className="flex items-center gap-4 border border-dashed border-bleu-hellio-500 rounded-lg px-4 py-3">
          <Button shape="round" className="border-bleu-hellio-500 text-bleu-hellio-500">
            {browseLabel}
          </Button>
          <span className="text-sm text-gray-400">{maxSizeLabel}</span>
        </div>
      </Upload>

      {fileSizeErrors.length > 0 && (
        <div className="flex flex-col gap-2">
          {fileSizeErrors.map((error, index) => (
            <Alert
              key={index}
              showIcon
              message={error}
              type="error"
              className="bg-transparent border-none text-rouge-copro-10 text-left px-0"
            />
          ))}
        </div>
      )}
    </div>
  );
}
